import React, { useEffect, useState } from 'react';
import {
    collection, query, where, limit, getDocs, getCountFromServer,
} from 'firebase/firestore';
import { History, X, Phone, PhoneIncoming, PhoneMissed, Clock, AlertCircle } from 'lucide-react';
import { db } from './firebase';

/**
 * ─────────────────────────────────────────────────────────────
 *  Call History (keypad থেকে করা কলের রেকর্ড) — order এর সাথে দেখানো
 *
 *  কল সার্ভার প্রতিটা কল এখানে সেভ করে:
 *    • callRecords/{callId}                    ← সব কলের ফ্ল্যাট কপি (number = 01XXXXXXXXX)
 *    • orders/{orderId}/callHistory/{callId}   ← নাম্বার যে অর্ডারের, সেই অর্ডারের নিচেও
 *  এখানে দুটো জায়গা থেকেই পড়ে callId মিলিয়ে এক লিস্ট বানানো হয়, তাই
 *  অর্ডার Cancel / Return / Delivered যেটাই হোক, ওই নাম্বারের সব কল (রিসিভ হওয়া,
 *  No Answer, Busy, কেটে দেওয়া, ব্যর্থ — সব) দেখা যায়। শুধু পড়া হয়, কিছু বদলায় না।
 * ─────────────────────────────────────────────────────────────
 */

const BN_DIGITS = '০১২৩৪৫৬৭৮৯';

/** যেকোনো ফরম্যাটের নাম্বার → 01XXXXXXXXX (কল সার্ভারের toLocal এর মতোই) */
export const toLocalPhone = (raw) => {
    const t = String(raw || '').replace(/[০-৯]/g, (ch) => String(BN_DIGITS.indexOf(ch)));
    let d = t.replace(/\D/g, '');
    if (d.startsWith('0088')) d = d.slice(2);
    if (d.startsWith('88') && d.length >= 12) d = d.slice(2);
    if (d.length === 10 && !d.startsWith('0')) d = '0' + d;
    return d;
};

const fmtDuration = (sec) => {
    const s = Math.max(0, Math.round(Number(sec) || 0));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r ? `${m}m ${r}s` : `${m}m`;
};

const STATUS_STYLE = {
    'Call Ended': 'bg-green-100 text-green-700',
    'No Answer': 'bg-amber-100 text-amber-700',
    'Busy': 'bg-amber-100 text-amber-700',
    'Rejected': 'bg-red-100 text-red-700',
    'Unavailable': 'bg-red-100 text-red-700',
    'Call Failed': 'bg-red-100 text-red-700',
    'Cancelled': 'bg-gray-100 text-gray-600',
};

const callMillis = (c) => {
    if (c.timestamp) return Number(c.timestamp) * 1000;
    const ms = c.createdAt?.toMillis?.();
    if (ms) return ms;
    const t = Date.parse(String(c.start_time || '').replace(' ', 'T'));
    return Number.isNaN(t) ? 0 : t;
};

/** callRecords + orders/{id}/callHistory → callId দিয়ে ডুপ্লিকেট বাদ, নতুন আগে */
async function loadCalls(orderId, phone) {
    const local = toLocalPhone(phone);
    const jobs = [];
    if (local.length >= 10) {
        jobs.push(getDocs(query(collection(db, 'callRecords'), where('number', '==', local), limit(300))));
    }
    if (orderId) {
        jobs.push(getDocs(collection(db, 'orders', orderId, 'callHistory')));
    }
    const results = await Promise.allSettled(jobs);
    const map = new Map();
    let failed = 0;
    results.forEach((r) => {
        if (r.status === 'fulfilled') {
            r.value.forEach((d) => { if (!map.has(d.id)) map.set(d.id, { id: d.id, ...d.data() }); });
        } else {
            failed += 1;
            console.error('Call history read failed:', r.reason);
        }
    });
    const calls = Array.from(map.values()).sort((a, b) => callMillis(b) - callMillis(a));
    return { calls, failed, total: results.length };
}

export function CallHistoryModal({ order, onClose }) {
    const [state, setState] = useState({ loading: true, calls: [], failed: 0, total: 0 });
    const phone = order.customerInfo?.phone;

    useEffect(() => {
        let alive = true;
        loadCalls(order.id, phone).then((r) => { if (alive) setState({ loading: false, ...r }); });
        return () => { alive = false; };
    }, [order.id, phone]);

    const { loading, calls, failed, total } = state;
    const answered = calls.filter((c) => c.answered).length;
    const talk = calls.reduce((s, c) => s + (Number(c.duration) || 0), 0);

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-3xl border border-gray-200 max-h-[85vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-gray-900">Call History</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-900"><X size={24} /></button>
                </div>

                <div className="mb-4 p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
                    <p>Customer: <span className="font-semibold">{order.customerInfo?.name || 'N/A'}</span></p>
                    <p>Phone: <span className="font-semibold">{phone || 'N/A'}</span></p>
                    <p>Order ID: <span className="font-mono text-orange-600">{order.orderId}</span> <span className="text-gray-500">({order.status})</span></p>
                </div>

                {!loading && failed > 0 && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg flex gap-2">
                        <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                        <span>
                            {failed === total
                                ? 'Call history পড়া যায়নি — Firestore rules এ callRecords এবং orders/{id}/callHistory পড়ার অনুমতি লাগবে।'
                                : 'একটা সোর্স থেকে পড়া যায়নি (Firestore rules দেখুন), তাই কিছু কল বাদ থাকতে পারে।'}
                        </span>
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-500"></div>
                    </div>
                ) : calls.length === 0 ? (
                    <p className="text-center text-gray-500 py-10">এই নাম্বারে এখনো কোনো কল করা হয়নি</p>
                ) : (
                    <>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="bg-orange-50 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-gray-900">{calls.length}</div>
                                <div className="text-xs text-gray-500">Total calls</div>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-gray-900">{answered}</div>
                                <div className="text-xs text-gray-500">Answered</div>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3 text-center">
                                <div className="text-xl font-bold text-gray-900">{fmtDuration(talk)}</div>
                                <div className="text-xs text-gray-500">Talk time</div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            {calls.map((c) => {
                                const Icon = c.answered ? PhoneIncoming : (c.status === 'No Answer' || c.status === 'Busy') ? PhoneMissed : Phone;
                                const linkedHere = Array.isArray(c.linkedTo) && c.linkedTo.includes(`orders/${order.id}`);
                                return (
                                    <div key={c.id} className="border border-gray-200 rounded-lg p-3">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex items-center gap-2">
                                                <Icon size={16} className="text-orange-500" />
                                                <span className={`text-xs px-2 py-1 rounded ${STATUS_STYLE[c.status] || 'bg-gray-100 text-gray-600'}`}>
                                                    {c.status || 'Unknown'}
                                                </span>
                                                {linkedHere && (
                                                    <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-700">এই অর্ডারে</span>
                                                )}
                                            </div>
                                            <div className="text-sm text-gray-700">{c.start_time || '—'}</div>
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
                                            <span className="flex items-center gap-1"><Clock size={12} /> {fmtDuration(c.duration)}</span>
                                            {c.username && <span>By: {c.username}</span>}
                                            {c.answered && c.endedBy && (
                                                <span>Ended by: {c.endedBy === 'admin' ? 'Admin' : 'Customer'}</span>
                                            )}
                                            {c.failReason && <span>Reason: {c.failReason}</span>}
                                        </div>
                                        {c.recordingUrl && (
                                            <audio controls preload="none" src={c.recordingUrl} className="mt-2 w-full h-9" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/** Actions কলামের বাটন: কলের সংখ্যা ব্যাজে দেখায়, চাপলে পুরো history খোলে */
export default function CallHistoryButton({ order }) {
    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(null);
    const local = toLocalPhone(order.customerInfo?.phone);

    useEffect(() => {
        let alive = true;
        if (local.length < 10) return undefined;
        getCountFromServer(query(collection(db, 'callRecords'), where('number', '==', local)))
            .then((s) => { if (alive) setCount(s.data().count); })
            .catch(() => { /* rules/নেটওয়ার্ক সমস্যা হলে ব্যাজ দেখাবে না, বাটন ঠিকই কাজ করবে */ });
        return () => { alive = false; };
    }, [local]);

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="relative p-2 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded-lg transition duration-200"
                title="Call History"
            >
                <History size={18} />
                {count > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-semibold bg-orange-500 text-white rounded-full">
                        {count}
                    </span>
                )}
            </button>
            {open && <CallHistoryModal order={order} onClose={() => setOpen(false)} />}
        </>
    );
}
